import { RootState } from "@store/reducers";
import { ContractType, PositionLevel } from "@utils/constants";
import {
  Briefcase,
  Layers,
  FileText,
  MapPin,
  PencilLineIcon,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getClassificationSummary } from "@utils/classification";
import AddEditTargetModal from "../components/AddEditTargetModal";

export const positionOptions = [
  { label: "Intern", value: PositionLevel.INTERN },
  { label: "Fresher", value: PositionLevel.FRESHER },
  { label: "Junior", value: PositionLevel.JUNIOR },
  { label: "Associate", value: PositionLevel.ASSOCIATE },
  { label: "Mid", value: PositionLevel.MID },
  { label: "Senior", value: PositionLevel.SENIOR },
  { label: "Lead", value: PositionLevel.LEAD },
  { label: "Manager", value: PositionLevel.MANAGER },
  { label: "Sr. Manager", value: PositionLevel.SR_MANAGER },
  { label: "Director", value: PositionLevel.DIRECTOR },
  { label: "Sr. Director", value: PositionLevel.SR_DIRECTOR },
  { label: "VP", value: PositionLevel.VP },
  { label: "SVP", value: PositionLevel.SVP },
  { label: "EVP", value: PositionLevel.EVP },
  { label: "Founder", value: PositionLevel.FOUNDER },
];

export const contractOptions = [
  { label: "Contract", value: ContractType.CONTRACT },
  { label: "Part-Time", value: ContractType.PARTTIME },
  { label: "Permanent", value: ContractType.PERMANENT },
];

const targetFieldRows = [
  [
    { key: "industry", label: "Target Industry", icon: Briefcase },
    { key: "seniority", label: "Target Seniority", icon: TrendingUp },
    { key: "location", label: "Preferred Location", icon: MapPin },
  ],
  [
    { key: "classification", label: "Classification", icon: Layers },
    { key: "contract", label: "Work Style", icon: FileText },
    { key: "salary", label: "Salary Expectation", icon: Wallet },
  ],
];

const TargetSection = () => {
  const aspirationDetails = useSelector(
    (state: RootState) => state.auth.aspirationDetails,
  );

  const [isEditing, setIsEditing] = useState(false);

  // Display values derived from the fetched aspiration details
  const targetRows = useMemo(() => {
    const valueByKey: Record<string, string> = {
      industry: aspirationDetails?.industryType || "N/A",
      seniority:
        positionOptions.find(
          (position) => position.value === aspirationDetails?.positionLevel,
        )?.label || "N/A",
      location: aspirationDetails?.location || "N/A",
      salary:
        aspirationDetails?.salary?.min != null &&
        aspirationDetails?.salary?.max != null
          ? `£${Number(aspirationDetails.salary.min).toLocaleString()} - £${Number(
              aspirationDetails.salary.max,
            ).toLocaleString()}`
          : "N/A",
      company: aspirationDetails?.company || "N/A",
      classification: aspirationDetails?.classification?.main
        ? getClassificationSummary(aspirationDetails.classification)
        : "N/A",
      contract:
        contractOptions.find(
          (contract) => contract.value === aspirationDetails?.contractType,
        )?.label || "N/A",
    };

    return targetFieldRows.map((row) =>
      row.map((field) => ({ ...field, value: valueByKey[field.key] ?? "N/A" })),
    );
  }, [aspirationDetails]);

  return (
    <div className="flex flex-col border rounded-xl bg-white p-6 gap-3 relative">
      <div className="flex items-center justify-between">
        <span className="uppercase text-xs font-bold text-slate-600">
          My target role
        </span>
        <button
          onClick={() => setIsEditing(true)}
          className="hover:bg-gray-100 h-8 w-8 rounded-md flex items-center justify-center absolute right-3 top-3"
        >
          <PencilLineIcon className="h-5 w-5" />
        </button>
      </div>
      <span className="text-slate-800 text-3xl font-bold">
        {aspirationDetails?.jobTitle}
      </span>
      <div className="border-t flex flex-col gap-8 justify-between pt-5 mt-2">
        {targetRows?.map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="flex justify-between">
              {row?.map((target) => {
                return (
                  <div key={target?.key} className="flex gap-2.5 flex-1">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-gray-50">
                      <target.icon className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="flex flex-col gap-0.5 mt-1.5">
                      <span className="text-xs font-bold text-gray-600">
                        {target?.label}
                      </span>
                      <span className="text-xs font-medium text-slate-800 line-clamp-2 capitalize">
                        {target?.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      <AddEditTargetModal
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        aspirationDetails={aspirationDetails}
      />
    </div>
  );
};

export default TargetSection;
